import _ from "lodash";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {FormPersister} from "./FormPersister";

const DEFAULT_FILTER_DEBOUNCE_MS = 500;

export interface SearchFilterTableBindingOptions<TValues, TQuery> {
    persistentIdent: string;
    defaultValues: TValues | (() => TValues);
    normalizeValues: (values: unknown) => TValues;
    createQuery: (values: TValues) => TQuery;
    submitQuery: (query: TQuery) => void;
    immediateMode: boolean;
}

export interface SearchFilterTableBinding<TValues> {
    values: TValues;
    onChange: (values: unknown) => void;
}

export function useSearchFilterTableBinding<TValues, TQuery>(
    options: SearchFilterTableBindingOptions<TValues, TQuery>
): SearchFilterTableBinding<TValues> {
    const filterPersister = useMemo(() => new FormPersister(options.persistentIdent), [options.persistentIdent]);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const submitValues = useCallback((nextValues: TValues) => {
        optionsRef.current.submitQuery(optionsRef.current.createQuery(nextValues));
    }, []);

    const loadedValues = useMemo(() => loadInitialValues(), [filterPersister]);
    const [values, setValues] = useState<TValues>(loadedValues);
    const valuesRef = useRef(values);

    const debouncedSubmit = useMemo(() => _.debounce(submitValues, DEFAULT_FILTER_DEBOUNCE_MS), [submitValues]);

    useEffect(() => {
        valuesRef.current = loadedValues;
        setValues(loadedValues);
        submitValues(loadedValues);
    }, [loadedValues, submitValues]);

    useEffect(() => {
        return () => debouncedSubmit.cancel();
    }, [debouncedSubmit]);

    useEffect(() => {
        if (!options.immediateMode) {
            debouncedSubmit.flush();
        }
    }, [debouncedSubmit, options.immediateMode]);

    return {
        values,
        onChange
    };

    function onChange(rawValues: unknown) {
        const nextValues = optionsRef.current.normalizeValues(rawValues);

        if (_.isEqual(nextValues, valuesRef.current)) {
            return;
        }

        valuesRef.current = nextValues;
        setValues(nextValues);

        scheduleSubmit(nextValues);
    }

    function scheduleSubmit(nextValues: TValues) {
        filterPersister.saveState(nextValues);

        if (!optionsRef.current.immediateMode) {
            debouncedSubmit.cancel();
            submitValues(nextValues);

            return;
        }

        debouncedSubmit(nextValues);
    }

    function loadInitialValues() {
        if (!filterPersister.hasState()) {
            return resolveDefaultValues();
        }

        try {
            const persistedValues = filterPersister.loadValues();

            if (!_.isEmpty(persistedValues)) {
                return optionsRef.current.normalizeValues(persistedValues);
            }
        } catch (error) {
            if (process.env.NODE_ENV !== "production") {
                console.warn(`Ignoring unreadable persisted filter state for ${filterPersister.ident}.`, error);
            }
        }

        return resolveDefaultValues();
    }

    function resolveDefaultValues() {
        return typeof optionsRef.current.defaultValues === "function"
            ? (optionsRef.current.defaultValues as () => TValues)()
            : optionsRef.current.defaultValues;
    }
}
